import type {
  Achievement,
  Catalog,
  DashboardData,
  MapCareer,
  Rank,
  ReplayDetail,
  ReplaySummary,
  Season,
} from "../types";

const maps = [
  { id: "crossroads", name: "Crossroads" },
  { id: "harbor", name: "Harbor" },
  { id: "u-turn", name: "U-Turn" },
  { id: "four-seasons", name: "Four Seasons" },
  { id: "wrecked-battlefield", name: "Wrecked Battlefield" },
  { id: "dusty-bridges", name: "Dusty Bridges" },
  { id: "badlands-ii", name: "Badlands II" },
];

const modes = [
  { id: "easy", name: "Easy" },
  { id: "casual", name: "Casual" },
  { id: "intermediate", name: "Intermediate" },
  { id: "molten", name: "Molten" },
  { id: "fallen", name: "Fallen" },
  { id: "frost", name: "Frost" },
  { id: "hardcore", name: "Hardcore" },
];

const replaySeed: Array<Pick<ReplaySummary, "id" | "careerReplayNumber" | "seasonReplayNumber" | "date" | "map" | "mode" | "teamSize" | "result" | "status" | "overallGrade" | "xpAwarded" | "isPromotionTrial" | "isCareerMilestone">> = [
  { id: "S4-027", careerReplayNumber: 177, seasonReplayNumber: 27, date: "2026-07-27", map: maps[0]!, mode: modes[4]!, teamSize: "Squad", result: "Win", status: "Under Review", overallGrade: "A", xpAwarded: 82, isPromotionTrial: true, isCareerMilestone: false },
  { id: "S4-026", careerReplayNumber: 176, seasonReplayNumber: 26, date: "2026-07-24", map: maps[4]!, mode: modes[6]!, teamSize: "Trio", result: "Loss", status: "Under Review", overallGrade: "B", xpAwarded: 54, isPromotionTrial: false, isCareerMilestone: false },
  { id: "S4-025", careerReplayNumber: 175, seasonReplayNumber: 25, date: "2026-07-22", map: maps[5]!, mode: modes[3]!, teamSize: "Solo", result: "Win", status: "Reviewed", overallGrade: "S", xpAwarded: 96, isPromotionTrial: false, isCareerMilestone: false },
  { id: "S4-024", careerReplayNumber: 174, seasonReplayNumber: 24, date: "2026-07-20", map: maps[2]!, mode: modes[5]!, teamSize: "Duo", result: "Win", status: "Promotion Trial", overallGrade: "SS", xpAwarded: 110, isPromotionTrial: true, isCareerMilestone: false },
  { id: "S4-023", careerReplayNumber: 173, seasonReplayNumber: 23, date: "2026-07-18", map: maps[1]!, mode: modes[2]!, teamSize: "Squad", result: "Win", status: "Career Milestone", overallGrade: "A", xpAwarded: 78, isPromotionTrial: false, isCareerMilestone: true },
  { id: "S4-022", careerReplayNumber: 172, seasonReplayNumber: 22, date: "2026-07-16", map: maps[3]!, mode: modes[6]!, teamSize: "Trio", result: "Loss", status: "Reviewed", overallGrade: "C", xpAwarded: 32, isPromotionTrial: false, isCareerMilestone: false },
  { id: "S4-021", careerReplayNumber: 171, seasonReplayNumber: 21, date: "2026-07-14", map: maps[6]!, mode: modes[4]!, teamSize: "Duo", result: "Win", status: "Reviewed", overallGrade: "B", xpAwarded: 59, isPromotionTrial: false, isCareerMilestone: false },
  { id: "S4-020", careerReplayNumber: 170, seasonReplayNumber: 20, date: "2026-07-12", map: { id: "sky-islands", name: "Sky Islands" }, mode: modes[4]!, teamSize: "Solo", result: "Loss", status: "Reviewed", overallGrade: "C", xpAwarded: 27, isPromotionTrial: false, isCareerMilestone: false },
];

export const demoReplays: ReplaySummary[] = replaySeed.map((replay, index) => ({
  ...replay,
  seasonId: 4,
  seasonNumber: 4,
  youtubeUrl: "https://www.youtube.com/watch?v=aqz-KE-bpKQ",
  isSeasonFinale: false,
  achievementCount: index === 0 ? 1 : index % 3 === 0 ? 1 : 0,
  achievementNames: index === 0 ? ["Clean Economy"] : [],
}));

export const demoAchievements: Achievement[] = [
  { id: "clean-economy", name: "Clean Economy", description: "Maintain 90%+ economy efficiency in a win.", icon: "trending-up", category: "challenge", isHidden: false, targetValue: 1, currentValue: 1, earnedAt: "2026-07-28", replayId: "S4-027", earned: true },
  { id: "fallen-specialist", name: "Fallen Specialist", description: "Complete ten Fallen-mode reviews.", icon: "shield", category: "difficulty", isHidden: false, targetValue: 10, currentValue: 10, earnedAt: "2026-07-23", replayId: "S4-025", earned: true },
  { id: "five-win-streak", name: "Five Win Streak", description: "Win five reviewed replays in a row.", icon: "flame", category: "progress", isHidden: false, targetValue: 5, currentValue: 5, earnedAt: "2026-07-23", replayId: "S4-025", earned: true },
  { id: "first-sss-plus", name: "First SSS+", description: "Reach the maximum replay grade.", icon: "crown", category: "challenge", isHidden: false, targetValue: 1, currentValue: 1, earnedAt: "2025-01-29", replayId: "S1-010", earned: true },
  { id: "first-void-legend", name: "First Void Legend", description: "A hidden career achievement.", icon: "orbit", category: "hidden", isHidden: true, targetValue: 1, currentValue: 0, earnedAt: null, replayId: null, earned: false },
  { id: "best-replay-ever", name: "Best Replay Ever", description: "Set a new career-high replay grade.", icon: "medal", category: "challenge", isHidden: false, targetValue: 1, currentValue: 0, earnedAt: null, replayId: null, earned: false },
];

export const demoDashboard: DashboardData = {
  career: {
    rank: "Diamond II",
    tier: "Diamond",
    division: "II",
    rankOrder: 14,
    divisionXp: 68,
    xpRequired: 100,
    careerXp: 4268,
    voidLegendLevel: 0,
    nextRank: "Diamond I",
    season: { id: 4, number: 4, name: "Season 4 · Diamond Path" },
  },
  stats: {
    totalReviews: 177,
    wins: 129,
    losses: 48,
    averageGrade: "A",
    highestGrade: "SSS+",
    winStreak: 6,
  },
  promotionGoals: [
    { id: "goal-xp", description: "Earn 100 division XP", targetValue: 100, currentValue: 68, unit: "XP", completed: false, required: true, position: 1 },
    { id: "goal-average", description: "Maintain an A− average or higher", targetValue: 1, currentValue: 1, unit: "", completed: true, required: true, position: 2 },
    { id: "goal-win-rate", description: "Achieve a 60% season win rate", targetValue: 60, currentValue: 72, unit: "%", completed: true, required: true, position: 3 },
  ],
  recentReplays: demoReplays.slice(0, 5),
  recentAchievements: demoAchievements.slice(0, 3),
  gradeTrend: Array.from({ length: 20 }, (_, index) => ({
    replay: 158 + index,
    grade: (["A", "B", "A", "S", "A", "SS", "A", "S", "B", "A"] as const)[index % 10]!,
    score: [5, 4, 5, 6, 5, 7, 5, 6, 4, 5][index % 10]!,
  })),
};

export const demoCatalog: Catalog = {
  maps,
  modes,
  towers: ["Commander", "DJ Booth", "Accelerator", "Ranger", "Farm", "Engineer", "Pursuit", "Warden"].map((name) => ({ id: name.toLowerCase().replaceAll(" ", "-"), name })),
  consumables: ["Damage Flag", "Air Strike", "Supply Drop", "Nuke"].map((name) => ({ id: name.toLowerCase().replaceAll(" ", "-"), name })),
  reviewCategories: ["Economy", "Tower Placement", "Upgrade Timing", "Decision Making", "Boss Preparation", "Adaptability", "Teamplay", "Consumable Usage", "Efficiency"].map((name, index) => ({ id: name.toLowerCase().replaceAll(" ", "-"), name, sortOrder: index + 1 })),
  achievements: demoAchievements.map(({ id, name, category, isHidden }) => ({ id, name, category, isHidden })),
  seasons: [
    { id: 4, number: 4, name: "Season 4 · Diamond Path", status: "active" },
    { id: 3, number: 3, name: "Season 3 · Breakpoint", status: "archived" },
    { id: 2, number: 2, name: "Season 2 · Rising Signal", status: "archived" },
    { id: 1, number: 1, name: "Season 1 · First Contact", status: "archived" },
  ],
};

export const demoReplayDetail: ReplayDetail = {
  ...demoReplays[0]!,
  commentary: {
    thinking: "Focus on economy first, then set up the Accelerator chain for the mid-game spike.",
    strategy: "Use Commander and DJ Booth coverage to keep upgrades efficient.",
    problems: "I got overwhelmed on wave 37 and Air Strike was on cooldown.",
    noticed: "The final boss setup had excellent lane coverage.",
    questions: "Was Accelerator timing the main issue, or should I have moved Rangers?",
  },
  towers: ["Commander", "DJ Booth", "Accelerator", "Ranger", "Farm"].map((name, index) => ({ id: name.toLowerCase().replaceAll(" ", "-"), name, position: index + 1 })),
  consumables: ["Damage Flag", "Air Strike"].map((name, index) => ({ id: name.toLowerCase().replaceAll(" ", "-"), name, position: index + 1 })),
  review: {
    id: "review-177",
    overallGrade: "A",
    writtenReview: "Great run overall. Your economy was stable and you hit key power spikes at the right times. Tower placement was excellent, especially around the mid choke and boss lanes.\n\nWatch your upgrade timing in the mid game—delays there cost you some efficiency. Also, save Air Strike for the final threat wave.",
    xpAwarded: 82,
    categories: demoCatalog.reviewCategories.map((category) => ({ ...category, grade: "A" })),
    categoryGrades: { economy: "A", "tower-placement": "S", "upgrade-timing": "B", "decision-making": "A", "boss-preparation": "A", adaptability: "B", teamplay: "S", "consumable-usage": "A", efficiency: "A" },
    highlights: [{ id: "h1", content: "Strong early economy and farm setup" }, { id: "h2", content: "Excellent position on final boss" }],
    mistakes: [{ id: "m1", content: "Late Accelerator upgrades" }, { id: "m2", content: "Air Strike used too early" }],
    improvementGoals: [{ id: "g1", content: "Improve mid-game upgrade timing" }, { id: "g2", content: "Save consumables for critical waves" }],
    rankBeforeName: "Diamond II",
    rankAfterName: "Diamond II",
  },
  achievements: [{ id: "clean-economy", name: "Clean Economy", description: "Maintain 90%+ economy efficiency in a win.", icon: "trending-up", earnedAt: "2026-07-28" }],
};

export const demoSeasons: Season[] = [
  { id: 4, number: 4, name: "Season 4 · Diamond Path", startedAt: "2026-03-25", endedAt: null, status: "active", replayOfSeasonId: null, reviewedReplays: 27, wins: 20, losses: 7, seasonXp: 1874, averageGrade: "A" },
  { id: 3, number: 3, name: "Season 3 · Breakpoint", startedAt: "2025-10-26", endedAt: "2026-03-24", status: "archived", replayOfSeasonId: "S3-041", reviewedReplays: 50, wins: 38, losses: 12, seasonXp: 3260, averageGrade: "A" },
  { id: 2, number: 2, name: "Season 2 · Rising Signal", startedAt: "2025-05-29", endedAt: "2025-10-25", status: "archived", replayOfSeasonId: "S2-046", reviewedReplays: 50, wins: 37, losses: 13, seasonXp: 2998, averageGrade: "B" },
  { id: 1, number: 1, name: "Season 1 · First Contact", startedAt: "2025-01-01", endedAt: "2025-05-28", status: "archived", replayOfSeasonId: "S1-050", reviewedReplays: 50, wins: 34, losses: 16, seasonXp: 2712, averageGrade: "B" },
];

const rankNames = ["Bronze III", "Bronze II", "Bronze I", "Silver III", "Silver II", "Silver I", "Gold III", "Gold II", "Gold I", "Platinum III", "Platinum II", "Platinum I", "Diamond III", "Diamond II", "Diamond I", "Master III", "Master II", "Master I", "Grandmaster III", "Grandmaster II", "Grandmaster I", "Legend", "Void Legend"];
export const demoRanks: Rank[] = rankNames.map((name, index) => ({
  id: index + 1,
  rankOrder: index + 1,
  name,
  tier: name.split(" ")[0]!,
  division: name.split(" ")[1] ?? null,
  xpRequired: 100,
  hasLevels: name === "Void Legend",
  current: name === "Diamond II",
  voidLegendLevel: 0,
}));

export const demoMapCareer: MapCareer[] = maps.map((map, mapIndex) => ({
  ...map,
  modes: modes.map((mode, modeIndex) => ({
    ...mode,
    status: (mapIndex + modeIndex) % 3 === 0 ? "Not Started" : "Complete",
    completedAt: (mapIndex + modeIndex) % 3 === 0 ? null : "2026-07-28",
    replayId: (mapIndex + modeIndex) % 3 === 0 ? null : "S4-027",
  })),
}));
